import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const OrderDetailModal = ({ order, isOpen, onClose, onTrackOrder, onReorder, onDownloadInvoice, onReturnItems }) => {
  if (!isOpen || !order) return null;

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'delivered':
        return 'bg-success text-success-foreground';
      case 'shipped':
        return 'bg-primary text-primary-foreground';
      case 'processing':
        return 'bg-warning text-warning-foreground';
      case 'cancelled':
        return 'bg-error text-error-foreground';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(price);
  };

  const getProgressSteps = (status) => {
    const steps = [
      { key: 'processing', label: 'Processing', icon: 'Clock' },
      { key: 'shipped', label: 'Shipped', icon: 'Truck' },
      { key: 'delivered', label: 'Delivered', icon: 'CheckCircle' }
    ];

    const statusOrder = ['processing', 'shipped', 'delivered'];
    const currentIndex = statusOrder.indexOf(status.toLowerCase());

    return steps.map((step, index) => ({
      ...step,
      completed: index <= currentIndex,
      current: index === currentIndex
    }));
  };

  const progressSteps = getProgressSteps(order.status);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Order Details</h2>
            <p className="text-sm text-muted-foreground">Order #{order.orderNumber}</p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            iconName="X"
            onClick={onClose}
            className="h-8 w-8 p-0"
          />
        </div>

        <div className="p-6 space-y-6">
          {/* Order Status & Progress */}
          <div className="bg-muted/30 rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <div>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(order.status)}`}>
                  {order.status}
                </span>
                <p className="text-sm text-muted-foreground mt-1">
                  Ordered on {formatDate(order.date)}
                </p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-foreground">{formatPrice(order.total)}</p>
                <p className="text-sm text-muted-foreground">{order.itemCount} items</p>
              </div>
            </div>

            {/* Progress Tracker */}
            {order.status.toLowerCase() !== 'cancelled' && (
              <div className="flex items-center justify-between">
                {progressSteps.map((step, index) => (
                  <div key={step.key} className="flex items-center">
                    <div className={`
                      flex items-center justify-center w-8 h-8 rounded-full border-2 transition-all
                      ${step.completed 
                        ? 'bg-success border-success text-success-foreground' 
                        : step.current
                        ? 'bg-primary border-primary text-primary-foreground'
                        : 'bg-background border-border text-muted-foreground'
                      }
                    `}>
                      <Icon name={step.icon} size={16} />
                    </div>
                    <div className="ml-2">
                      <p className={`text-xs font-medium ${
                        step.current ? 'text-primary' : step.completed ? 'text-success' : 'text-muted-foreground'
                      }`}>
                        {step.label}
                      </p>
                    </div>
                    {index < progressSteps.length - 1 && (
                      <div className={`w-16 h-0.5 mx-4 ${
                        step.completed ? 'bg-success' : 'bg-border'
                      }`} />
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Order Items */}
          <div>
            <h3 className="text-lg font-semibold text-foreground mb-4">Items Ordered</h3>
            <div className="space-y-3">
              {order.items.map((item, index) => (
                <div key={index} className="flex items-center gap-4 p-3 bg-muted/30 rounded-lg">
                  <Image
                    src={item.image}
                    alt={item.name}
                    className="w-16 h-16 rounded-lg object-cover"
                  />
                  <div className="flex-1">
                    <h4 className="font-medium text-foreground">{item.name}</h4>
                    <p className="text-sm text-muted-foreground">
                      {item.variant && `${item.variant} • `}Qty: {item.quantity}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {formatPrice(item.price)} each
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-foreground">
                      {formatPrice(item.quantity * item.price)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Order Summary */}
          <div className="bg-muted/30 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-foreground mb-3">Order Summary</h3>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Subtotal</span>
                <span className="text-foreground">{formatPrice(order.subtotal)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Shipping</span>
                <span className="text-foreground">{formatPrice(order.shipping)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Tax</span>
                <span className="text-foreground">{formatPrice(order.tax)}</span>
              </div>
              <div className="border-t border-border pt-2">
                <div className="flex justify-between font-semibold">
                  <span className="text-foreground">Total</span>
                  <span className="text-foreground">{formatPrice(order.total)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Shipping & Payment Info */}
          <div className="grid md:grid-cols-2 gap-6">
            {/* Shipping Address */}
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-3">Shipping Address</h3>
              <div className="bg-muted/30 rounded-lg p-4">
                <div className="text-sm text-muted-foreground space-y-1">
                  <p className="font-medium text-foreground">{order.shippingAddress.name}</p>
                  <p>{order.shippingAddress.street}</p>
                  <p>{order.shippingAddress.city}, {order.shippingAddress.state} {order.shippingAddress.zip}</p>
                  <p>{order.shippingAddress.country}</p>
                </div>
              </div>
            </div>

            {/* Payment Method */}
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-3">Payment Method</h3>
              <div className="bg-muted/30 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <Icon name="CreditCard" size={20} />
                  <div>
                    <p className="text-sm font-medium text-foreground">
                      {order.paymentMethod.type}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Ending in {order.paymentMethod.last4}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Tracking Information */}
          {order.trackingNumber && (
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-3">Tracking Information</h3>
              <div className="bg-muted/30 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <Icon name="Truck" size={20} />
                  <div>
                    <p className="text-sm font-medium text-foreground">
                      Tracking Number: {order.trackingNumber}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Carrier: {order.carrier}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-3 pt-4 border-t border-border">
            {order.status.toLowerCase() !== 'cancelled' && (
              <Button
                variant="outline"
                iconName="Package"
                iconPosition="left"
                onClick={() => onTrackOrder(order.id)}
              >
                Track Package
              </Button>
            )}
            
            <Button
              variant="outline"
              iconName="RotateCcw"
              iconPosition="left"
              onClick={() => onReorder(order.id)}
            >
              Reorder Items
            </Button>
            
            <Button
              variant="outline"
              iconName="Download"
              iconPosition="left"
              onClick={() => onDownloadInvoice(order.id)}
            >
              Download Invoice
            </Button>
            
            {order.status.toLowerCase() === 'delivered' && (
              <Button
                variant="outline"
                iconName="RotateCcw"
                iconPosition="left"
                onClick={() => onReturnItems(order.id)}
              >
                Return Items
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderDetailModal;